import { toJsDate } from "../utils/toJsDate";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityFeedData,
} from "../contexts/ActivityContext";
import { type ActivityRequest } from "../types";
import {
  ArrowLeftIcon,
  MessageSquareIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  CalendarIcon,
  HeartIcon,
  SparklesIcon,
} from "../components/icons/FeatherIcons";
import { motion, Variants, AnimatePresence } from "framer-motion";
import BottomNavigation from "../components/BottomNavigation";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useCachedImage } from "../hooks/useCachedImage";
import { ActivityRichText } from "../utils/activityRichText";

// Floating sparkles animation component
const FloatingSparkles: React.FC = () => {
  const sparkles = Array.from({ length: 8 }, (_, i) => i);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {sparkles.map((i) => (
        <motion.div
          key={i}
          className="absolute text-yellow-400"
          initial={{
            x: Math.random() * window.innerWidth,
            y: window.innerHeight + 20,
            opacity: 0,
            scale: 0,
          }}
          animate={{
            y: -100,
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0.5],
            rotate: [0, 45, -45, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            delay: i * 0.3,
            repeat: Infinity,
            repeatDelay: 4,
          }}
        >
          <SparklesIcon className="w-5 h-5" />
        </motion.div>
      ))}
    </div>
  );
};

// Confetti particles
const ConfettiParticle: React.FC<{ delay: number }> = ({ delay }) => (
  <motion.div
    className="absolute w-3 h-3 rounded-full"
    style={{
      background: `hsl(${Math.random() * 360}, 70%, 60%)`,
      left: `${Math.random() * 100}%`,
    }}
    initial={{ y: -20, opacity: 0, rotate: 0 }}
    animate={{
      y: window.innerHeight + 20,
      opacity: [0, 1, 1, 0],
      rotate: 360,
      x: [0, Math.random() * 100 - 50],
    }}
    transition={{
      duration: 3 + Math.random() * 2,
      delay,
      ease: "easeOut",
    }}
  />
);

// Enhanced activity card with beautiful design
const ActivitySnippetCard: React.FC<{
  activity: ActivityRequest;
  onViewDetails?: () => void;
  cachedActivityImage?: string;
}> = ({ activity, onViewDetails, cachedActivityImage }) => {
  const formatDate = (dateString: string) => {
    const date = toJsDate(dateString);
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Generate bright gradient based on category
  const getBrightGradient = (category: string) => {
    const gradients = {
      Sports: "linear-gradient(135deg, #FF6B6B, #4ECDC4, #45B7D1)",
      Food: "linear-gradient(135deg, #FF9A56, #FF6B9D, #C44569)",
      Entertainment: "linear-gradient(135deg, #A8E6CF, #FFD93D, #6BCF7F)",
      Education: "linear-gradient(135deg, #4FACFE, #00F2FE, #43E97B)",
      Travel: "linear-gradient(135deg, #FA709A, #FEE140, #24FE41)",
      Social: "linear-gradient(135deg, #667eea, #764ba2, #f093fb)",
      Wellness: "linear-gradient(135deg, #ffecd2, #fcb69f, #ff8a80)",
      Arts: "linear-gradient(135deg, #a8edea, #fed6e3, #d299c2)",
      Technology: "linear-gradient(135deg, #00c6ff, #0072ff, #74b9ff)",
      Music: "linear-gradient(135deg, #ffeaa7, #fab1a0, #fd79a8)",
    };
    return (
      gradients[category as keyof typeof gradients] ||
      gradients["Entertainment"]
    );
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0, y: 30 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const shimmerVariants = {
    hidden: { x: -100, opacity: 0 },
    visible: {
      x: 400,
      opacity: [0, 1, 0],
      transition: {
        duration: 1.5,
        delay: 1,
        ease: "easeInOut" as const,
      },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      className="relative bg-gradient-to-br from-white/95 via-blue-50/90 to-purple-50/80 p-4 rounded-xl shadow-xl border border-white/80 backdrop-blur-lg overflow-hidden"
      whileHover={{
        scale: 1.01,
        y: -2,
        rotateY: 2,
        rotateX: 1,
        boxShadow: "0 15px 30px rgba(59, 130, 246, 0.15)",
        transition: { type: "spring", stiffness: 400, damping: 25 },
      }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Enhanced background decorations */}
      <motion.div
        className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 rounded-full -translate-y-6 translate-x-6 blur-md"
        animate={{
          rotate: [0, 360],
          opacity: [0.15, 0.35, 0.15],
          scale: [0.8, 1.2, 0.8],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-full translate-y-4 -translate-x-4 blur-md"
        animate={{
          rotate: [360, 0],
          opacity: [0.1, 0.25, 0.1],
          scale: [0.9, 1.3, 0.9],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      {/* Animated border glow */}
      <motion.div
        className="absolute inset-0 rounded-2xl border border-gradient-to-r from-primary/30 via-purple-500/30 to-blue-500/30"
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Simplified floating particles */}
      {[...Array(2)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1.5 h-1.5 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-full blur-sm"
          style={{
            top: `${25 + i * 35}%`,
            left: `${15 + i * 40}%`,
          }}
          animate={{
            y: [-8, 8, -8],
            x: [-3, 3, -3],
            opacity: [0.1, 0.5, 0.1],
          }}
          transition={{
            duration: 3 + i,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.7,
          }}
        />
      ))}

      <div className="relative z-10">
        {/* Compact header */}
        <div className="mb-2 -mx-4 -mt-4">
          <div className="relative overflow-hidden rounded-t-xl">
            {activity.imageUrl && cachedActivityImage ? (
              <div className="relative">
                <motion.img
                  src={cachedActivityImage}
                  alt={activity.title}
                  className="w-full h-16 object-cover"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-white/5"></div>
              </div>
            ) : (
              <div
                className="w-full h-16 relative overflow-hidden flex items-center justify-center"
                style={{ background: getBrightGradient(activity.category) }}
              >
                <motion.div
                  className="bg-white/25 backdrop-blur-sm p-2 rounded-md shadow-md border border-white/15"
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <SparklesIcon className="w-4 h-4 text-white drop-shadow-md" />
                </motion.div>
              </div>
            )}

            {/* Compact category badge */}
            <motion.div
              className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-md shadow-sm border border-white/50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="text-xs font-bold text-primary">
                {activity.category}
              </span>
            </motion.div>
          </div>
        </div>

        {/* Compact Activity Title */}
        <div className="mb-2">
          <motion.h3
            className="text-base font-bold text-gray-900 mb-1 leading-tight"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 150 }}
          >
            {activity.title}
          </motion.h3>
          <motion.div
            className="h-0.5 rounded-full mb-2 shadow-sm"
            initial={{ width: 0, opacity: 0 }}
            animate={{
              width: "100%",
              opacity: 1,
              background: [
                "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #3b82f6 100%)",
                "linear-gradient(90deg, #8b5cf6 0%, #ec4899 50%, #8b5cf6 100%)",
                "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #3b82f6 100%)",
              ],
            }}
            transition={{
              width: { delay: 0.5, duration: 1, ease: "easeOut" },
              opacity: { delay: 0.5, duration: 1, ease: "easeOut" },
              background: { duration: 3, repeat: Infinity, ease: "easeInOut" },
            }}
          ></motion.div>
          {activity.description && (
            <motion.p
              className="text-gray-600 text-xs leading-relaxed line-clamp-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
                <ActivityRichText text={activity.description} />
            </motion.p>
          )}
        </div>

        {/* Compact info grid */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <motion.div
            className="flex items-center bg-gradient-to-br from-blue-50/80 to-indigo-100/50 p-2 rounded-lg shadow-sm border border-blue-200/30 backdrop-blur-sm"
            whileHover={{
              scale: 1.02,
              y: -1,
              boxShadow: "0 8px 16px rgba(59, 130, 246, 0.1)",
              background:
                "linear-gradient(135deg, rgba(239, 246, 255, 0.9) 0%, rgba(224, 231, 255, 0.7) 100%)",
            }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            animate={{
              background: [
                "linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(224, 231, 255, 0.5) 100%)",
                "linear-gradient(135deg, rgba(224, 231, 255, 0.6) 0%, rgba(199, 210, 254, 0.6) 100%)",
                "linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(224, 231, 255, 0.5) 100%)",
              ],
            }}
          >
            <motion.div
              className="bg-gradient-to-br from-primary/20 to-blue-600/15 p-1.5 rounded-md mr-2.5 shadow-sm"
              animate={{
                background: [
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%)",
                  "linear-gradient(135deg, rgba(37, 99, 235, 0.25) 0%, rgba(29, 78, 216, 0.2) 100%)",
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <MapPinIcon className="w-3.5 h-3.5 text-primary" />
            </motion.div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-primary/50 uppercase tracking-wide mb-0.5">
                Location
              </p>
              <p className="font-bold text-gray-800 text-xs truncate">
                {activity.location}
              </p>
            </div>
          </motion.div>

          <motion.div
            className="flex items-center bg-gradient-to-br from-green-50/80 to-emerald-100/50 p-2 rounded-lg shadow-sm border border-green-200/30 backdrop-blur-sm"
            whileHover={{
              scale: 1.02,
              y: -1,
              boxShadow: "0 8px 16px rgba(34, 197, 94, 0.1)",
              background:
                "linear-gradient(135deg, rgba(240, 253, 244, 0.9) 0%, rgba(220, 252, 231, 0.7) 100%)",
            }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            animate={{
              background: [
                "linear-gradient(135deg, rgba(240, 253, 244, 0.8) 0%, rgba(220, 252, 231, 0.5) 100%)",
                "linear-gradient(135deg, rgba(220, 252, 231, 0.6) 0%, rgba(187, 247, 208, 0.6) 100%)",
                "linear-gradient(135deg, rgba(240, 253, 244, 0.8) 0%, rgba(220, 252, 231, 0.5) 100%)",
              ],
            }}
          >
            <motion.div
              className="bg-gradient-to-br from-green-500/20 to-emerald-600/15 p-1.5 rounded-md mr-2.5 shadow-sm"
              animate={{
                background: [
                  "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)",
                  "linear-gradient(135deg, rgba(5, 150, 105, 0.25) 0%, rgba(4, 120, 87, 0.2) 100%)",
                  "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)",
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
            >
              <CalendarIcon className="w-3.5 h-3.5 text-green-600" />
            </motion.div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-green-600/50 uppercase tracking-wide mb-0.5">
                Date
              </p>
              <p className="font-bold text-gray-800 text-xs truncate">
                {toJsDate(activity.dateTime).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Compact footer */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100/60">
          {/* Participant display - styled as info badge */}
          <motion.div
            className="flex items-center bg-gradient-to-r from-gray-50 to-blue-50/80 px-2 py-1.5 rounded-lg shadow-sm border border-gray-200/60 backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
          >
            <div className="bg-gradient-to-br from-primary/20 to-blue-600/15 p-1.5 rounded-md mr-2.5 shadow-sm">
              <UsersIcon className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">
                Members
              </p>
              <span className="font-bold text-sm text-gray-800">
                {activity.participants?.length || 1}/{activity.groupSize}
              </span>
            </div>
          </motion.div>

          {/* Action button - styled as primary CTA */}
          <motion.button
            className="bg-gradient-to-r from-primary to-blue-600 text-white font-semibold px-3 py-1.5 rounded-lg shadow-md border border-white/20 relative overflow-hidden cursor-pointer"
            whileHover={{
              scale: 1.05,
              y: -1,
              boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)",
              background: "linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)",
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            onClick={onViewDetails}
          >
            {/* Subtle shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-12"
              animate={{ x: [-100, 300] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
                repeatDelay: 1,
              }}
            />
            <span className="text-xs relative z-10 flex items-center gap-1.5">
              ✨ View Details
            </span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const MatchPage: React.FC = () => {
  // usePageTracking ('MatchPage');
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { activities } = useActivityFeedData();
  const { refreshSingleActivity } = useActivityActions();

  const routeState = location.state as
    | { prefetchedActivity?: ActivityRequest }
    | undefined;
  const prefetchedActivity =
    routeState?.prefetchedActivity?.id === activityId
      ? routeState.prefetchedActivity
      : null;
  const localActivity = activities.find((a) => a.id === activityId) || null;
  const initialActivity = prefetchedActivity || localActivity;

  const [activity, setActivity] = useState<ActivityRequest | null>(initialActivity);
  const [retryCount, setRetryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(!initialActivity);
  const [showConfetti, setShowConfetti] = useState(false);
  const retryCountRef = useRef(0);

  // Cache activity and user images
  const cachedActivityImage = useCachedImage(
    activity?.imageUrl || "",
    activity?.title || "",
    activity?.id || "",
  );
  const cachedCreatorImage = useCachedImage(
    activity?.creator?.profileImageUrl || "",
    activity?.creator?.username || "",
    activity?.creator?.id || "",
  );
  const cachedCurrentUserImage = useCachedImage(
    currentUser?.profileImageUrl || "",
    currentUser?.username || "",
    currentUser?.id || "",
  );

  // Trigger confetti after initial animation when activity is available
  useEffect(() => {
    if (activity) {
      const timer = setTimeout(() => setShowConfetti(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [activity]);

  useEffect(() => {
    retryCountRef.current = retryCount;
  }, [retryCount]);

  useEffect(() => {
    if (!activityId) {
      navigate("/");
      return;
    }

    const cached =
      prefetchedActivity ||
      localActivity;
    if (cached) {
      setActivity(cached);
      setIsLoading(false);
    }

    if (!cached) {
      setIsLoading(true);
    }
  }, [activityId, navigate, prefetchedActivity, localActivity]);

  useEffect(() => {
    if (!activityId) {
      navigate("/");
      return;
    }

    const hasImmediateData = Boolean(
      prefetchedActivity || localActivity || (activity?.id === activityId),
    );
    if (!hasImmediateData) {
      setIsLoading(true);
    }

    let isMounted = true;
    refreshSingleActivity(activityId)
      .then((result) => {
        if (isMounted) {
          if (result) {
            setActivity(result);
          }
          setRetryCount(0);
          retryCountRef.current = 0;
          if (!result && !hasImmediateData) {
            navigate("/");
          }
          if (!hasImmediateData) {
            setIsLoading(false);
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          if (!hasImmediateData) {
            setIsLoading(false);
          }
          const nextRetryCount = retryCountRef.current + 1;
          retryCountRef.current = nextRetryCount;
          setRetryCount(nextRetryCount);
          if (nextRetryCount >= 2 && !hasImmediateData) {
            navigate("/");
          }
        }
      });
    return () => {
      isMounted = false;
    };
  }, [activityId, navigate, refreshSingleActivity]);

  if (!activity || !currentUser || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full mx-auto mb-6"
          />
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl"
          >
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Loading activity...
            </h3>
            {isLoading && retryCount > 0 && (
              <p className="text-primary text-sm">
                Finding activity details (Retry {retryCount}/2)
              </p>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  const creator = activity.creator;

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 80, damping: 12 },
    },
  };

  const avatarVariants: Variants = {
    hidden: { opacity: 0, scale: 0.3, rotate: -180 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { type: "spring", stiffness: 100, damping: 8, delay: 0.8 },
    },
  };

  const connectionVariants: Variants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 1.5, delay: 1.2, ease: "easeInOut" },
    },
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 via-green-50 to-yellow-100 relative overflow-hidden"
      style={{ minHeight: "100%" }}
    >
      {/* Enhanced animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large flowing gradients */}
        <motion.div
          className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-r from-blue-300/30 via-purple-200/25 to-green-300/30 rounded-full blur-3xl"
          animate={{
            rotate: 360,
            scale: [1, 1.2, 0.9, 1.1, 1],
            x: [0, 50, -30, 20, 0],
            y: [0, -20, 40, -10, 0],
          }}
          transition={{
            rotate: { duration: 40, repeat: Infinity, ease: "linear" },
            scale: { duration: 8, repeat: Infinity, ease: "easeInOut" },
            x: { duration: 12, repeat: Infinity, ease: "easeInOut" },
            y: { duration: 15, repeat: Infinity, ease: "easeInOut" },
          }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-l from-yellow-300/30 via-orange-200/25 to-pink-300/25 rounded-full blur-3xl"
          animate={{
            rotate: -360,
            scale: [1, 1.15, 0.95, 1.08, 1],
            x: [0, -60, 40, -25, 0],
            y: [0, 30, -50, 15, 0],
          }}
          transition={{
            rotate: { duration: 45, repeat: Infinity, ease: "linear" },
            scale: { duration: 10, repeat: Infinity, ease: "easeInOut" },
            x: { duration: 14, repeat: Infinity, ease: "easeInOut" },
            y: { duration: 18, repeat: Infinity, ease: "easeInOut" },
          }}
        />
        {/* Additional floating orbs */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-32 h-32 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 30, -20, 0],
            y: [0, -40, 20, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/5 w-24 h-24 bg-gradient-to-r from-purple-400/25 to-pink-400/25 rounded-full blur-xl"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.2, 0.5, 0.2],
            x: [0, -25, 35, 0],
            y: [0, 35, -15, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Floating sparkles */}
      <FloatingSparkles />

      {/* Confetti */}
      <AnimatePresence>
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 20 }, (_, i) => (
              <ConfettiParticle key={i} delay={i * 0.1} />
            ))}
          </div>
        )}
      </AnimatePresence>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 h-full flex flex-col"
        style={{ minHeight: "100%" }}
      >
        {/* Responsive container: mobile-first with larger breakpoints */}
        <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto h-full flex flex-col px-4 pb-4">
          {/* Header */}
          <header className="w-full flex-shrink-0 mb-1">
            <motion.button
              variants={itemVariants}
              onClick={() => navigate("/")}
              className="text-gray-600 hover:text-gray-900 p-2 -ml-2 rounded-full transition-all duration-300 hover:bg-white/50 hover:shadow-lg"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </motion.button>
          </header>

          {/* Main content - balanced layout */}
          <main className="flex flex-col items-center text-center flex-1 px-4 pb-20 overflow-y-auto">
            {/* Balanced success message */}
            <motion.div variants={itemVariants} className="mt-2 mb-12">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  delay: 0.3,
                  type: "spring",
                  stiffness: 120,
                  damping: 12,
                }}
                className="w-16 h-16 bg-gradient-to-r from-primary to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl"
              >
                <UsersIcon className="w-8 h-8 text-white" />
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-2xl font-bold tracking-tight text-gray-800 leading-tight mb-3"
              >
                🎉 You're In!
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-base text-gray-600 max-w-sm leading-relaxed"
              >
                You've joined{" "}
                <span className="font-bold text-base bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent">
                  {creator.username}'s
                </span>{" "}
                activity
              </motion.p>
            </motion.div>

            {/* Balanced connection visualization with spacing */}
            <motion.div
              variants={itemVariants}
              className="relative w-full max-w-xs h-24 flex justify-center items-center mb-12 py-3"
            >
              {/* Connection line with better proportions */}
              <svg
                className="absolute w-full h-full"
                viewBox="0 0 300 96"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  d="M50 48 C 100 28, 200 68, 250 48"
                  stroke="url(#gradient)"
                  strokeWidth="4"
                  fill="none"
                  variants={connectionVariants}
                />
                <defs>
                  <linearGradient
                    id="gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="50%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#3B82F6" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Balanced user avatars */}
              <motion.div variants={avatarVariants} className="absolute left-0">
                <div className="text-center">
                  <motion.div
                    className="relative w-16 h-16 bg-gradient-to-br from-white to-blue-50 p-1 rounded-xl shadow-lg"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={cachedCreatorImage}
                      alt={creator.username}
                      className="w-full h-full object-cover rounded-lg border border-white/50"
                    />
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border border-white"></div>
                  </motion.div>
                  <motion.span
                    className="mt-1 inline-block font-medium text-xs bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-white/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                  >
                    {creator.username.length > 10
                      ? creator.username.slice(0, 10) + "..."
                      : creator.username}
                  </motion.span>
                </div>
              </motion.div>

              <motion.div
                variants={avatarVariants}
                className="absolute right-0"
              >
                <div className="text-center">
                  <motion.div
                    className="relative w-16 h-16 bg-gradient-to-br from-white to-green-50 p-1 rounded-xl shadow-lg"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={cachedCurrentUserImage}
                      alt="You"
                      className="w-full h-full object-cover rounded-lg border border-white/50"
                    />
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border border-white"></div>
                  </motion.div>
                  <motion.span
                    className="mt-1 inline-block font-medium text-xs bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-white/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                  >
                    You
                  </motion.span>
                </div>
              </motion.div>
            </motion.div>

            {/* Subtle visual separator */}
            <motion.div
              variants={itemVariants}
              className="w-20 h-0.5 bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mb-6 rounded-full"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 1.5, duration: 0.8, ease: "easeOut" }}
            />

            {/* Central prominent action buttons */}
            <motion.div
              variants={itemVariants}
              className="w-full max-w-lg mb-8 flex flex-col gap-5"
            >
              <motion.button
                onClick={() => navigate(`/chat/${activity.chatId}`)}
                className="w-full bg-gradient-to-r from-primary via-blue-500 to-blue-600 text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25 border border-white/20 relative overflow-hidden"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 20px 40px rgba(59, 130, 246, 0.3)",
                }}
                whileTap={{ scale: 0.98 }}
                animate={{
                  background: [
                    "linear-gradient(90deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%)",
                    "linear-gradient(90deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)",
                    "linear-gradient(90deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%)",
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                  animate={{ x: [-200, 400] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    repeatDelay: 1,
                  }}
                />
                <MessageSquareIcon className="w-5 h-5 mr-3 relative z-10" />
                <span className="text-base font-bold relative z-10">
                  💬 Start Chat
                </span>
              </motion.button>

              <motion.button
                onClick={() => navigate("/")}
                className="w-full bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150 text-gray-700 font-semibold py-3.5 px-6 rounded-xl border border-gray-200 shadow-sm transition-all duration-300 flex items-center justify-center"
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-sm font-semibold">
                  🔍 Explore More Activities
                </span>
              </motion.button>
            </motion.div>

            {/* Ultra-compact activity card with spacing from nav */}
            <motion.div
              variants={itemVariants}
              className="w-full max-w-sm mb-6"
            >
              <ActivitySnippetCard
                activity={activity}
                onViewDetails={() => navigate(`/activity/${activity.id}`)}
                cachedActivityImage={cachedActivityImage}
              />
            </motion.div>
          </main>
        </div>
        {/* End of responsive container */}
      </motion.div>

      {/* Bottom Navigation - Fixed Position */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <BottomNavigation />
      </div>
    </div>
  );
};

export default MatchPage;
